"use client";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import centerImg from "./logo.png";
import nickelodeonLogo from "./logo nickelodeon.png";
import tireGliderImg from "./coco.png";
import bikeGliderImg from "./lime e bike.png";
import scooterGliderImg from "./bird scooter.png";
import PhotoModule from "./components/PhotoModule";
import PlaylistEmbed from "./components/PlaylistEmbed";
import GoToCoords from "./components/GoToCoords";
import Notes from "./components/Notes";
import Bio from "./components/Bio";

const PLANE_SIZE = 100000;
const HALF = PLANE_SIZE / 2;
const EDGE_THRESHOLD = 1500;

// world → DOM helpers (centered system: 0,0 at visual center)
const toScreenX = (x) => HALF + x;
const toScreenY = (y) => HALF + y;

function TireGlider({
  src,
  width = 160,
  maxSpeed = 900, // px/sec
  maxAccel = 2800, // px/sec^2
  retargetRadius = 80, // px before retargeting (world coords)
  redrawHz = 60, // paint throttle
  // Trail options
  trailSeconds = 3, // how long tracks remain visible
  trailGap = 20, // min distance (px) moved before adding a new mark
  trackWidth = 22, // distance between left/right tires
  trackDot = 8, // size of each mark dot (px)
  trackOpacity = 0.9, // initial opacity of freshest mark
  trackColor = "0,0,0", // RGB for tire mark (we fade alpha over time)
  singleTrack = false, // true = one center line of marks (e.g. a bike), false = two tracks
  startX = 800, // initial world position, so multiple gliders don't spawn stacked
  startY = -600,
  flipWithDirection = false, // true = mirror image horizontally based on travel direction
  initialFacing = -1, // starting orientation before the first direction change: 1 = normal, -1 = mirrored
  flipSign = 1, // set to -1 to invert which way the image mirrors for a given travel direction
}) {
  // Paint position only; physics state lives in refs (WORLD COORDS)
  const [paintPos, setPaintPos] = useState({ x: startX, y: startY });
  const [facing, setFacing] = useState(initialFacing); // 1 = normal, -1 = mirrored

  // Motion refs (WORLD COORDS)
  const posRef = useRef({ x: startX, y: startY });
  const facingRef = useRef(initialFacing);
  const velRef = useRef({ x: 0, y: 0 });
  const targetRef = useRef({ x: 0, y: 0 });
  const lastTsRef = useRef(0);
  const accTimeRef = useRef(0);

  // Trail refs (array of marks; each is { lx, ly, rx, ry, t }) in WORLD COORDS
  const trailRef = useRef([]);
  const lastTrailPosRef = useRef({ x: paintPos.x, y: paintPos.y });

  const pickNewTarget = () => {
    const margin = 1200;
    const min = -HALF + margin;
    const max = HALF - margin;
    targetRef.current = {
      x: Math.random() * (max - min) + min,
      y: Math.random() * (max - min) + min,
    };
  };

  useEffect(() => {
    pickNewTarget();

    let raf = 0;
    const frameInterval = 1 / Math.max(1, redrawHz); // seconds per paint

    const tick = (ts) => {
      if (!lastTsRef.current) lastTsRef.current = ts;
      const dt = Math.max(0.001, (ts - lastTsRef.current) / 1000); // seconds
      lastTsRef.current = ts;

      // --- load state (world)
      let { x, y } = posRef.current;
      let { x: vx, y: vy } = velRef.current;
      const { x: tx, y: ty } = targetRef.current;

      // --- vector to target
      let dx = tx - x;
      let dy = ty - y;
      const dist = Math.hypot(dx, dy);

      if (dist < retargetRadius || Math.random() < 0.001) {
        pickNewTarget();
      }

      // direction to target
      if (dist > 1e-6) {
        dx /= dist;
        dy /= dist;
      } else {
        dx = 0;
        dy = 0;
      }

      // desired velocity
      const desiredVx = dx * maxSpeed;
      const desiredVy = dy * maxSpeed;

      // steering with accel clamp
      let steerX = desiredVx - vx;
      let steerY = desiredVy - vy;
      const steerMag = Math.hypot(steerX, steerY);
      const maxStep = maxAccel * dt;
      if (steerMag > maxStep) {
        steerX = (steerX / steerMag) * maxStep;
        steerY = (steerY / steerMag) * maxStep;
      }

      // update velocity
      vx += steerX;
      vy += steerY;

      // clamp speed
      const vMag = Math.hypot(vx, vy);
      if (vMag > maxSpeed) {
        vx = (vx / vMag) * maxSpeed;
        vy = (vy / vMag) * maxSpeed;
      }

      // integrate (world)
      let nx = x + vx * dt;
      let ny = y + vy * dt;

      // wrap in WORLD coords (range: -HALF .. +HALF)
      if (nx < -HALF) nx += PLANE_SIZE;
      if (ny < -HALF) ny += PLANE_SIZE;
      if (nx > HALF) nx -= PLANE_SIZE;
      if (ny > HALF) ny -= PLANE_SIZE;

      // --- record tire marks if we've moved enough
      const lastMark = lastTrailPosRef.current;
      const moved = Math.hypot(nx - lastMark.x, ny - lastMark.y);
      if (moved >= trailGap && vMag > 1) {
        // unit velocity direction
        let ux = vx,
          uy = vy;
        const um = Math.hypot(ux, uy);
        if (um > 1e-6) {
          ux /= um;
          uy /= um;
        } else {
          ux = 1;
          uy = 0;
        }
        // perpendicular to motion (left/right)
        const px = -uy;
        const py = ux;

        const half = singleTrack ? 0 : trackWidth / 2;
        const lx = nx + px * half;
        const ly = ny + py * half;
        const rx = nx - px * half;
        const ry = ny - py * half;

        trailRef.current.push({ lx, ly, rx, ry, t: performance.now() / 1000 });
        lastTrailPosRef.current = { x: nx, y: ny };
      }

      // prune old marks (keep only the last `trailSeconds`)
      const nowS = performance.now() / 1000;
      const cutoff = nowS - trailSeconds;
      if (trailRef.current.length > 0) {
        let i = 0;
        while (i < trailRef.current.length && trailRef.current[i].t < cutoff)
          i++;
        if (i > 0) trailRef.current.splice(0, i);
      }

      // --- commit motion (world)
      posRef.current = { x: nx, y: ny };
      velRef.current = { x: vx, y: vy };

      // Update facing direction (with deadzone so near-vertical movement doesn't flicker)
      if (flipWithDirection) {
        if (vx > 5) facingRef.current = -1 * flipSign;
        else if (vx < -5) facingRef.current = 1 * flipSign;
      }

      // Throttle paints
      accTimeRef.current += dt;
      if (accTimeRef.current >= frameInterval) {
        accTimeRef.current = 0;
        setPaintPos(posRef.current);
        if (flipWithDirection) setFacing(facingRef.current);
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [
    maxSpeed,
    maxAccel,
    retargetRadius,
    redrawHz,
    trailSeconds,
    trailGap,
    trackWidth,
    singleTrack,
    flipWithDirection,
    flipSign,
  ]);

  // Pull a snapshot of trail (WORLD coords)
  const trail = trailRef.current;
  const now = performance.now() / 1000;

  return (
    <>
      {/* Tire track dots (left & right) */}
      {trail.map((m, i) => {
        const age = now - m.t;
        const k = Math.max(0, 1 - age / trailSeconds); // 1 → 0 fade
        const alpha = (trackOpacity * k).toFixed(3);
        const styleCommon = {
          position: "absolute",
          width: trackDot,
          height: trackDot * 0.7, // slightly oval like a tire patch
          transform: "translate(-50%, -50%)",
          borderRadius: trackDot / 3,
          background: `rgba(${trackColor}, ${alpha})`,
          pointerEvents: "none",
          userSelect: "none",
          filter: "blur(0.2px)",
          zIndex: 5,
        };
        return (
          <React.Fragment key={i}>
            <div
              style={{
                ...styleCommon,
                left: toScreenX(m.lx),
                top: toScreenY(m.ly),
              }}
            />
            {!singleTrack && (
              <div
                style={{
                  ...styleCommon,
                  left: toScreenX(m.rx),
                  top: toScreenY(m.ry),
                }}
              />
            )}
          </React.Fragment>
        );
      })}

      {/* The glider image itself (convert world → DOM) */}
      <img
        src={src}
        alt="glider"
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          transform: `translate3d(${toScreenX(paintPos.x)}px, ${toScreenY(
            paintPos.y
          )}px, 0) translate(-50%, -50%) scaleX(${facing})`,
          width,
          height: "auto",
          objectFit: "contain",
          pointerEvents: "none",
          userSelect: "none",
          zIndex: 8,
          filter: "drop-shadow(0 6px 18px rgba(0,0,0,0.18))",
          willChange: "transform",
        }}
        draggable={false}
      />
    </>
  );
}

export default function InfiniteCanvasSite() {
  // Shared UI constants
  const GREEN_BG = "#426b4a";
  const hudButtonStyle = (pressed) => ({
    padding: "6px 10px",
    minWidth: 72,
    fontSize: 12,
    fontFamily: '"MS Sans Serif", Tahoma, Arial, sans-serif',
    color: "#111827",
    background: pressed ? "rgba(240,240,240,0.98)" : "rgba(255,255,255,0.92)",
    border: "1px solid #c7c7c7",
    borderRadius: 6,
    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
    cursor: "pointer",
    lineHeight: "1.2",
  });
  // Shared style for the giant PHOTOS/WORK/BIO section labels
  const bigLabelStyle = {
    fontFamily:
      "system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    fontWeight: 700,
    fontSize: 500,
    color: "#f0dfcf",
    letterSpacing: 1.5,
  };
  // Standard ref (holds the element)
  const viewportRef = useRef(null);
  const isHomingRef = useRef(false);
  const [homePressed, setHomePressed] = useState(false);
  const [locPressed, setLocPressed] = useState(false);

  // Set custom page icons using the bundled logo.png
  useEffect(() => {
    const head = document.head;
    if (!head) return;
    const ensureLink = (rel) => {
      let el = head.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        head.appendChild(el);
      }
      return el;
    };
    const favicon = ensureLink("icon");
    favicon.setAttribute("type", "image/png");
    favicon.setAttribute("href", nickelodeonLogo);

    const shortcut = ensureLink("shortcut icon");
    shortcut.setAttribute("type", "image/png");
    shortcut.setAttribute("href", nickelodeonLogo);

    const apple = ensureLink("apple-touch-icon");
    apple.setAttribute("href", nickelodeonLogo);
  }, []);

  const ARROW_COUNT = 4;
  // Compute squiggle radius so the rays start just outside the center image
  // (center image width is set below to 512). This keeps the squiggles close
  // to the logo while guarding against overlap.
  const CENTER_IMAGE_DISPLAY_WIDTH = 512; // matches the center img width below
  const SQUIGGLE_GAP_FROM_IMAGE = 24; // px gap between image edge and squiggle start
  const ARROW_RADIUS = Math.max(
    Math.round(CENTER_IMAGE_DISPLAY_WIDTH / 2 + SQUIGGLE_GAP_FROM_IMAGE),
    80
  );
  const SQUIGGLE_LENGTH = 25000; // how long each squiggle ray is
  const SQUIGGLE_WAVE = 40; // amplitude of the wiggle
  const [cursorXY, setCursorXY] = useState(null);
  // CONTACT link font size (px) — change this value to adjust CONTACT text size
  const CONTACT_FONT_SIZE = 1000;
  // Shared style for the giant CONTACT/POETRY links
  const bigLinkStyle = {
    color: "#0000EE",
    textDecoration: "underline",
    background: "transparent",
    border: "none",
    padding: 0,
    fontWeight: "normal",
    cursor: "pointer",
    display: "inline",
    fontFamily:
      "system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    fontSize: `${CONTACT_FONT_SIZE}px`,
  };

  function updateCursorFromEvent(e) {
    const el = viewportRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const absX = el.scrollLeft + (e.clientX - rect.left);
    const absY = el.scrollTop + (e.clientY - rect.top);
    // convert to world coords (center = 0,0)
    const worldX = absX - HALF;
    const worldY = absY - HALF;
    setCursorXY({ x: worldX, y: worldY });
  }

  // Callback ref (fires when React attaches/removes the element)
  const setViewportRef = (node) => {
    viewportRef.current = node;
  };

  // Scroll to a world coordinate (x,y). Centers that world point in the viewport.
  const goToWorld = (x, y) => {
    const el = viewportRef.current;
    if (!el) return;
    const targetLeft = Math.round(HALF + x - el.clientWidth / 2);
    const targetTop = Math.round(HALF + y - el.clientHeight / 2);
    const clampedLeft = Math.max(
      0,
      Math.min(el.scrollWidth - el.clientWidth, targetLeft)
    );
    const clampedTop = Math.max(
      0,
      Math.min(el.scrollHeight - el.clientHeight, targetTop)
    );
    // use smooth scrolling for nicer UX; fallback to immediate if unavailable
    try {
      el.scrollTo({ left: clampedLeft, top: clampedTop, behavior: "smooth" });
    } catch (e) {
      el.scrollLeft = clampedLeft;
      el.scrollTop = clampedTop;
    }
    lastScrollRef.current = { left: clampedLeft, top: clampedTop };
  };

  // Renders outward-pointing squiggly rays around the center image
  const renderCenterArrows = () => {
    const items = Array.from({ length: ARROW_COUNT }, (_, i) => i);
    const step = (2 * Math.PI) / ARROW_COUNT;

    const makeSquigglePathD = () =>
      Array.from({ length: 20 })
        .map((_, j) => {
          const px = (SQUIGGLE_LENGTH / 20) * j;
          const py = SQUIGGLE_WAVE * Math.sin((j / 2) * Math.PI);
          return `${j === 0 ? "M" : "L"} ${px} ${py + SQUIGGLE_WAVE * 2}`;
        })
        .join(" ");

    return items.map((i) => {
      const theta = i * step;
      // Nudge the vertical (top and bottom) squiggles closer to the logo
      // i=1 → down (below), i=3 → up (above)
      const RAY_INSET = 120; // how many pixels closer to the center
      const localRadius =
        i === 1 || i === ARROW_COUNT - 1
          ? ARROW_RADIUS - RAY_INSET
          : ARROW_RADIUS;
      const x = HALF + localRadius * Math.cos(theta);
      const y = HALF + localRadius * Math.sin(theta);
      const rotationDeg = (theta * 180) / Math.PI;

      // Limit the hit width for squiggle #3 (index 2) and the last squiggle so
      // their hover only triggers when over the visible portion of the wavy
      // line. Other squiggles keep the full hit width to preserve UX.
      const hitWidth =
        i === 2 || i === ARROW_COUNT - 1
          ? Math.min(600, SQUIGGLE_LENGTH)
          : SQUIGGLE_LENGTH;

      // compute the world-space endpoint along the same theta direction —
      // matches the actual last drawn point in makeSquigglePathD (j=19 of 20,
      // so the visible line stops 5% short of SQUIGGLE_LENGTH) and uses this
      // ray's own localRadius so it lands exactly on the visible line's tip
      const drawnLength = (SQUIGGLE_LENGTH / 20) * 19;
      const endWorldX = Math.round(
        (localRadius + drawnLength) * Math.cos(theta)
      );
      const endWorldY = Math.round(
        (localRadius + drawnLength) * Math.sin(theta)
      );

      return (
        <div
          key={i}
          style={{
            position: "absolute",
            left: x,
            top: y,
            width: hitWidth,
            height: 60,
            transform: `translate(-50%, -50%) rotate(${rotationDeg}deg)`,
            transformOrigin: "center center",
            // Disable pointer events on the broad rotated box so only the path itself is interactive
            pointerEvents: "none",
            zIndex: 12,
            background: "transparent",
          }}
        >
          <div
            className="squiggle-group"
            style={{
              position: "absolute",
              // align the group's left edge with the positioned point so the
              // squiggle extends outward from the ring (prevents crossing
              // through the center image)
              left: "50%",
              top: "50%",
              transform: "translate(0, -50%)",
              // always visible now (no hover gating)
              opacity: 1,
              pointerEvents: "none", // events handled by transparent hit path
            }}
          >
            {/* the wavy line */}
            <svg
              viewBox={`0 0 ${SQUIGGLE_LENGTH} ${SQUIGGLE_WAVE * 4}`}
              style={{
                width: SQUIGGLE_LENGTH,
                height: SQUIGGLE_WAVE * 4,
                overflow: "visible",
                pointerEvents: "none",
              }}
            >
              <path
                d={makeSquigglePathD()}
                stroke="#ffffff"
                strokeWidth="2.5"
                fill="none"
                style={{
                  strokeDasharray: 8,
                  animation: "squiggle-move 3s linear infinite",
                }}
              />
              {/* Transparent thicker path to capture hover & click strictly along the line */}
              <path
                d={makeSquigglePathD()}
                stroke="transparent"
                strokeWidth="28" /* widen interactive area for usability */
                fill="none"
                style={{ cursor: "pointer" }}
                pointerEvents="stroke"
                onClick={() => goToWorld(endWorldX, endWorldY)}
              />
            </svg>

            {/* arrowhead removed: squiggles now render without triangle tips */}
          </div>

          {/* labels removed: squiggles render without numeric labels */}
        </div>
      );
    });
  };

  // Smoothly glide back to the true visual center; duration scales with distance
  const glideHome = () => {
    const el = viewportRef.current;
    if (!el) return;

    const startL = el.scrollLeft;
    const startT = el.scrollTop;

    const centerL = Math.max(0, HALF - el.clientWidth / 2);
    const centerT = Math.max(0, HALF - el.clientHeight / 2);

    const dx = centerL - startL;
    const dy = centerT - startT;
    const distance = Math.hypot(dx, dy); // pixels

    const duration = Math.min(1600, Math.max(320, 420 + distance * 0.4));

    // Ease in-out cubic (smooth, natural)
    const ease = (t) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    isHomingRef.current = true;

    const t0 = performance.now();

    const step = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      const e = ease(p);

      el.scrollLeft = startL + dx * e;
      el.scrollTop = startT + dy * e;

      // keep delta math consistent for any listeners
      lastScrollRef.current = { left: el.scrollLeft, top: el.scrollTop };

      if (p < 1) {
        requestAnimationFrame(step);
      } else {
        isHomingRef.current = false;
      }
    };

    requestAnimationFrame(step);
  };

  const lastScrollRef = useRef({ left: HALF, top: HALF });

  // Notes position in WORLD COORDS (center = 0,0); draggable by pointer
  // initial note position (WORLD COORDS). Set to -5000,-5000 so Locations starts off-center
  const [notePos, setNotePos] = useState({ x: -5000, y: -5000 });
  const noteDragRef = useRef({
    active: false,
    clickOffsetX: 0,
    clickOffsetY: 0,
  });

  const onNotePointerMove = (ev) => {
    if (!noteDragRef.current.active) return;
    const el = viewportRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const absX = el.scrollLeft + (ev.clientX - rect.left);
    const absY = el.scrollTop + (ev.clientY - rect.top);
    const newCenterAbsX = absX - noteDragRef.current.clickOffsetX;
    const newCenterAbsY = absY - noteDragRef.current.clickOffsetY;
    const newWorldX = newCenterAbsX - HALF;
    const newWorldY = newCenterAbsY - HALF;
    setNotePos({ x: newWorldX, y: newWorldY });
  };

  const onNotePointerUp = (ev) => {
    noteDragRef.current.active = false;
    window.removeEventListener("pointermove", onNotePointerMove);
    window.removeEventListener("pointerup", onNotePointerUp);
  };

  const startNoteDrag = (e) => {
    // Don't start dragging if the user clicked inside an input/textarea
    const tgt = e.target || e.srcElement;
    try {
      if (
        tgt &&
        (tgt.tagName === "TEXTAREA" || (tgt.closest && tgt.closest("textarea")))
      ) {
        return; // allow typing or selection inside the textarea
      }
    } catch (er) {
      // ignore any DOM errors and proceed
    }
    const el = viewportRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const absX = el.scrollLeft + (e.clientX - rect.left);
    const absY = el.scrollTop + (e.clientY - rect.top);
    const noteCenterAbsX = toScreenX(notePos.x);
    const noteCenterAbsY = toScreenY(notePos.y);
    const clickOffsetX = absX - noteCenterAbsX;
    const clickOffsetY = absY - noteCenterAbsY;
    noteDragRef.current = { active: true, clickOffsetX, clickOffsetY };
    window.addEventListener("pointermove", onNotePointerMove);
    window.addEventListener("pointerup", onNotePointerUp);
    // prevent other handlers while dragging
    e.preventDefault();
  };

  // Center ASAP when the node is available to avoid a null ref race
  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) {
      return;
    }
    const centerLeft = Math.max(0, HALF - el.clientWidth / 2);
    const centerTop = Math.max(0, HALF - el.clientHeight / 2);
    el.scrollLeft = centerLeft;
    el.scrollTop = centerTop;
    lastScrollRef.current = { left: el.scrollLeft, top: el.scrollTop };
  }, []);

  // Safety: also center on mount (after paint) in case layout sizes weren’t ready
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) {
      return;
    }
    requestAnimationFrame(() => {
      const centerLeft = Math.max(0, HALF - el.clientWidth / 2);
      const centerTop = Math.max(0, HALF - el.clientHeight / 2);
      el.scrollLeft = centerLeft;
      el.scrollTop = centerTop;
      lastScrollRef.current = { left: el.scrollLeft, top: el.scrollTop };
    });
  }, []);

  const recenterIfNeeded = (el) => {
    if (isHomingRef.current) return; // don't snap while we're animating home

    const maxLeft = el.scrollWidth - el.clientWidth;
    const maxTop = el.scrollHeight - el.clientHeight;

    const distLeft = el.scrollLeft;
    const distTop = el.scrollTop;
    const distRight = maxLeft - el.scrollLeft;
    const distBottom = maxTop - el.scrollTop;

    const nearAnyEdge =
      distLeft < EDGE_THRESHOLD ||
      distTop < EDGE_THRESHOLD ||
      distRight < EDGE_THRESHOLD ||
      distBottom < EDGE_THRESHOLD;

    if (nearAnyEdge) {
      const centerLeft = Math.max(0, HALF - el.clientWidth / 2);
      const centerTop = Math.max(0, HALF - el.clientHeight / 2);

      requestAnimationFrame(() => {
        el.scrollLeft = centerLeft;
        el.scrollTop = centerTop;
        lastScrollRef.current = { left: el.scrollLeft, top: el.scrollTop };
      });
    }
  };

  const onScroll = () => {
    const el = viewportRef.current;
    if (!el) {
      return;
    }

    const { left: prevLeft, top: prevTop } = lastScrollRef.current;
    const currLeft = el.scrollLeft;
    const currTop = el.scrollTop;

    if (currLeft !== prevLeft || currTop !== prevTop) {
      lastScrollRef.current = { left: currLeft, top: currTop };
    }

    recenterIfNeeded(el);
  };

  return (
    <div
      // Use inline styles so this works even without Tailwind
      style={{
        position: "relative",
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        background: GREEN_BG,
        color: "#111827",
        fontFamily:
          "system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
      }}
    >
      {/* Left-top control group: Home + Locations (inline) */}
      <div
        style={{
          position: "absolute",
          left: 16,
          top: 16,
          zIndex: 30, // above in-world content (e.g. the giant CONTACT/POETRY links) so it stays clickable
          display: "flex",
          flexDirection: "column",
          gap: 8,
          alignItems: "flex-start",
        }}
      >
        <button
          onClick={glideHome}
          onMouseDown={() => setHomePressed(true)}
          onMouseUp={() => setHomePressed(false)}
          onMouseLeave={() => setHomePressed(false)}
          style={hudButtonStyle(homePressed)}
          title="Return to center"
          aria-label="Home"
        >
          home
        </button>

        <button
          onClick={() => goToWorld(notePos.x, notePos.y)}
          onMouseDown={() => setLocPressed(true)}
          onMouseUp={() => setLocPressed(false)}
          onMouseLeave={() => setLocPressed(false)}
          style={hudButtonStyle(locPressed)}
          title="Go to locations"
          aria-label="Locations"
        >
          locations
        </button>
      </div>

      {/* Go-to coordinates widget (below home button) — placed without an outer HUD box */}
      <div
        style={{
          position: "absolute",
          right: 16,
          top: 56,
          zIndex: 30, // above in-world content (e.g. the giant CONTACT/POETRY links) so it stays clickable
          display: "flex",
          flexDirection: "column",
          gap: 6,
          alignItems: "flex-end",
          pointerEvents: "auto",
        }}
      >
        <GoToCoords onGo={(x, y) => goToWorld(x, y)} />
      </div>

      {/* Cursor HUD (world coords: center = 0,0) - moved next to Home button */}
      <div
        style={{
          position: "absolute",
          right: 16,
          top: 16,
          zIndex: 20,
          padding: "6px 10px",
          fontSize: 12,
          fontFamily: '"MS Sans Serif", Tahoma, Arial, sans-serif',
          background: "rgba(255,255,255,0.92)",
          border: "1px solid #c7c7c7",
          borderRadius: 6,
          boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
          pointerEvents: "none",
          minWidth: 160,
          textAlign: "right",
        }}
        title="Cursor position (world coords, center = 0,0)"
      >
        {cursorXY ? (
          <>
            x: {Math.round(cursorXY.x)} px&nbsp;&nbsp; y:{" "}
            {Math.round(cursorXY.y)} px
          </>
        ) : (
          <>x: —&nbsp;&nbsp; y: —</>
        )}
      </div>
      {/* Scrollable viewport */}
      <div
        ref={setViewportRef}
        onScroll={onScroll}
        onPointerMove={updateCursorFromEvent}
        onMouseMove={updateCursorFromEvent} // fallback
        onMouseLeave={() => setCursorXY(null)}
        style={{
          position: "absolute",
          inset: 0,
          overflow: "scroll",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
        className="hide-scrollbars"
      >
        <div
          style={{
            width: PLANE_SIZE,
            height: PLANE_SIZE,
            background: GREEN_BG,
            position: "relative",
          }}
        >
          {/* Center image (DOM center = HALF,HALF) */}
          <img
            src={centerImg}
            alt="center"
            style={{
              position: "absolute",
              left: HALF,
              top: HALF,
              transform: "translate(-50%, -50%)",
              width: 512, // ← adjust size
              height: "auto",
              objectFit: "contain",
              pointerEvents: "auto",
              zIndex: 10,
            }}
          />
          {/* Small notes module placed near the logo (draggable) */}
          <div
            style={{
              position: "absolute",
              left: toScreenX(notePos.x),
              top: toScreenY(notePos.y),
              transform: "translate(-50%, -50%)",
              zIndex: 16,
              cursor: "move",
            }}
            onPointerDown={startNoteDrag}
          >
            <Notes width={300} height={140} />
          </div>

          {/* PHOTOS label placed on the squiggle ray's y=0 line, starting just
              past where that ray ends so the text doesn't cross the line */}
          <div
            style={{
              position: "absolute",
              left: toScreenX(ARROW_RADIUS + SQUIGGLE_LENGTH + 10),
              top: toScreenY(0),
              transform: "translate(-50%, -50%)",
              zIndex: 14,
            }}
          >
            <div style={bigLabelStyle}>PHOTOS</div>
          </div>
          {/* PhotoModule placed to the right of the PHOTOS label's right edge */}
          <div
            style={{
              position: "absolute",
              left: toScreenX(ARROW_RADIUS + SQUIGGLE_LENGTH + 1650),
              top: toScreenY(0),
              transform: "translate(-50%, -50%)",
              zIndex: 14,
            }}
          >
            <PhotoModule thumbSize={220} panelWidth={760} />
          </div>
          {/* WORK label placed on the squiggle ray's y=0 line, starting just
              past where that ray ends so the text doesn't cross the line */}
          <div
            style={{
              position: "absolute",
              left: toScreenX(-(ARROW_RADIUS + SQUIGGLE_LENGTH) + 256),
              top: toScreenY(0),
              transform: "translate(-50%, -50%)",
              zIndex: 14,
            }}
          >
            <div style={bigLabelStyle}>WORK</div>
          </div>
          {/* Playlist embed placed to the left of the WORK label's left edge */}
          <div
            style={{
              position: "absolute",
              left: toScreenX(-(ARROW_RADIUS + SQUIGGLE_LENGTH) - 968),
              top: toScreenY(1100),
              transform: "translate(-50%, -50%)",
              zIndex: 14,
              width: 460,
            }}
          >
            <PlaylistEmbed width={460} height={3000} />
          </div>
          {/* Bio module placed at world coords x=0, y=25000 (moved 350px closer to the squiggle line) */}
          <div
            style={{
              position: "absolute",
              left: toScreenX(0),
              top: toScreenY(25000 - 350),
              transform: "translate(-50%, -50%)",
              zIndex: 14,
            }}
          >
            <div
              style={{ ...bigLabelStyle, marginBottom: 12, textAlign: "center" }}
            >
              BIO
            </div>
            <Bio width={1500} fontSize={30} />
          </div>
          {/* CONTACT link placed at world coords x=5000, y=-6262 (moved 400px closer to the squiggle line) */}
          <div
            style={{
              position: "absolute",
              left: toScreenX(0),
              top: toScreenY(-25000 + 400),
              transform: "translate(-50%, -50%)",
              zIndex: 20,
            }}
          >
            <a
              href="mailto:sendcalebstuff@gmail.com"
              style={bigLinkStyle}
              aria-label="Contact Caleb via email"
            >
              CONTACT
            </a>
          </div>

          {/* POETRY link placed at world coords x=-5000, y=-5000 */}
          <div
            style={{
              position: "absolute",
              left: toScreenX(-5000),
              top: toScreenY(10000),
              transform: "translate(-50%, -50%)",
              zIndex: 20,
            }}
          >
            <a
              href="/poetry.html"
              style={bigLinkStyle}
              aria-label="Poetry"
            >
              POETRY
            </a>
          </div>
          {/* Ring of 7 squiggly outward rays around center */}
          {renderCenterArrows()}

          <TireGlider
            src={tireGliderImg}
            width={200}
            maxSpeed={300}
            maxAccel={1800}
            trailSeconds={3.5}
            trailGap={18}
            trackWidth={26}
            trackDot={10}
            trackOpacity={0.85}
          />

          <TireGlider
            src={bikeGliderImg}
            width={80}
            maxSpeed={380}
            maxAccel={2200}
            trailSeconds={3.5}
            trailGap={18}
            trackDot={8}
            trackOpacity={0.85}
            singleTrack
            startX={-800}
            startY={600}
            flipWithDirection
          />

          <TireGlider
            src={scooterGliderImg}
            width={150}
            maxSpeed={380}
            maxAccel={2200}
            trailSeconds={3.5}
            trailGap={18}
            trackDot={8}
            trackOpacity={0.85}
            singleTrack
            startX={800}
            startY={600}
            flipWithDirection
            initialFacing={-1}
            flipSign={-1}
          />
        </div>
      </div>

      {/* Developer/debug buttons removed */}
    </div>
  );
}
